import React, { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import './SystemStatusBanner.css';

interface SystemStatusBannerProps {
  onLoadLatestConflict?: () => Promise<void>;
  onKeepConflictAsCopy?: () => Promise<void>;
  onOverwriteConflictWithMine?: () => Promise<void>;
  conflictActionLoading?: boolean;
}

export default function SystemStatusBanner({
  onLoadLatestConflict,
  onKeepConflictAsCopy,
  onOverwriteConflictWithMine,
  conflictActionLoading = false,
}: SystemStatusBannerProps) {
  const { t } = useI18n();
  const { syncState, auth, requestSync } = useAuth();
  const [openHelp, setOpenHelp] = useState<'loadLatest' | 'keepAsCopy' | 'overwriteMine' | null>(null);
  const shouldShow =
    syncState.status === 'locked' ||
    syncState.status === 'conflict' ||
    syncState.status === 'failed' ||
    syncState.status === 'blocked' ||
    Boolean(auth?.authExpired);

  if (!shouldShow) {
    return null;
  }

  let label = t('status.online');
  if (syncState.status === 'syncing') {
    label = t('status.syncing');
  } else if (syncState.status === 'locked') {
    label = t('status.waitingForLock');
  } else if (syncState.status === 'conflict') {
    label = t('status.conflictDetected');
  } else if (syncState.status === 'failed') {
    label = t('status.syncFailed');
  } else if (syncState.status === 'blocked' || auth?.authExpired) {
    label = t('status.reloginRequired');
  }

  const bannerClassName = [
    'system-status-banner',
    syncState.status === 'conflict'
      ? 'system-status-banner--conflict'
      : syncState.status === 'locked'
        ? 'system-status-banner--locked'
      : syncState.status === 'failed'
        ? 'system-status-banner--failed'
        : syncState.status === 'blocked'
          ? 'system-status-banner--blocked'
          : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={bannerClassName}>
      <div className="system-status-banner__summary">
        <strong className="system-status-banner__status">{label}</strong>
        <span className="system-status-banner__count">
          {syncState.conflictCount > 0
            ? t('status.conflictChanges', { count: syncState.conflictCount })
            : syncState.pendingCount > 0
            ? t('status.pendingChanges', { count: syncState.pendingCount })
            : t('status.noPendingChanges')}
        </span>
        {syncState.lastError ? <span className="system-status-banner__message">{syncState.lastError}</span> : null}
      </div>
      <div className="system-status-banner__actions">
        {syncState.status === 'locked' ? (
          <span className="system-status-banner__note">{t('status.lockedWillRetry')}</span>
        ) : null}
        {syncState.status === 'conflict' ? (
          <>
            <div className="system-status-banner__action-group">
              <button
                type="button"
                className="system-status-banner__button"
                onClick={() => void onLoadLatestConflict?.()}
                disabled={conflictActionLoading}
              >
                {t('status.useCloudVersion')}
              </button>
              <button
                type="button"
                className="system-status-banner__info-button"
                onClick={() => setOpenHelp((current) => current === 'loadLatest' ? null : 'loadLatest')}
                aria-label={t('status.moreInfo')}
                aria-expanded={openHelp === 'loadLatest'}
                disabled={conflictActionLoading}
              >
                i
              </button>
              {openHelp === 'loadLatest' ? (
                <div className="system-status-banner__tooltip">{t('status.loadLatestHelp')}</div>
              ) : null}
            </div>
            <div className="system-status-banner__action-group">
              <button
                type="button"
                className="system-status-banner__button"
                onClick={() => void onKeepConflictAsCopy?.()}
                disabled={conflictActionLoading}
              >
                {t('status.saveMineAsCopy')}
              </button>
              <button
                type="button"
                className="system-status-banner__info-button"
                onClick={() => setOpenHelp((current) => current === 'keepAsCopy' ? null : 'keepAsCopy')}
                aria-label={t('status.moreInfo')}
                aria-expanded={openHelp === 'keepAsCopy'}
                disabled={conflictActionLoading}
              >
                i
              </button>
              {openHelp === 'keepAsCopy' ? (
                <div className="system-status-banner__tooltip">{t('status.keepAsCopyHelp')}</div>
              ) : null}
            </div>
            <div className="system-status-banner__action-group">
              <button
                type="button"
                className="system-status-banner__button"
                onClick={() => void onOverwriteConflictWithMine?.()}
                disabled={conflictActionLoading}
              >
                {t('status.replaceCloudWithMine')}
              </button>
              <button
                type="button"
                className="system-status-banner__info-button"
                onClick={() => setOpenHelp((current) => current === 'overwriteMine' ? null : 'overwriteMine')}
                aria-label={t('status.moreInfo')}
                aria-expanded={openHelp === 'overwriteMine'}
                disabled={conflictActionLoading}
              >
                i
              </button>
              {openHelp === 'overwriteMine' ? (
                <div className="system-status-banner__tooltip">{t('status.replaceCloudWithMineHelp')}</div>
              ) : null}
            </div>
          </>
        ) : null}
        {(syncState.status === 'locked' || syncState.status === 'failed') ? (
          <button type="button" className="system-status-banner__button system-status-banner__button--standalone" onClick={() => void requestSync()}>
            {t('status.syncNow')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
